import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  CheckCircle2, 
  Loader2, 
  XCircle,
  ArrowRight,
  Crown,
  Star,
  CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MembershipSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed, expired
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const pollPaymentStatus = async () => {
      if (!user) return;
      
      try {
        const token = await user.getIdToken();
        const response = await axios.get(
          `${API_URL}/payments/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setPaymentData(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling if still pending
        if (pollCount < maxPolls) {
          setPollCount(prev => prev + 1);
          setTimeout(() => pollPaymentStatus(), 2000);
        } else {
          // Timeout - check final status
          if (response.data.payment_status !== 'paid') {
            setStatus('failed');
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount >= maxPolls - 1) {
          setStatus('failed');
        } else {
          setPollCount(prev => prev + 1);
          setTimeout(() => pollPaymentStatus(), 2000);
        }
      }
    };

    if (user) {
      pollPaymentStatus();
    }
  }, [user, searchParams, pollCount]);

  const getTierIcon = () => {
    const tierName = paymentData?.metadata?.tier_name || '';
    if (tierName.includes('Founders')) return <Crown className="w-12 h-12 text-gold" />;
    return <Star className="w-12 h-12 text-gold" />;
  };

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      <div className="container-padding">
        <div className="max-w-lg mx-auto">
          <Card className="bg-white shadow-lg">
            <CardContent className="p-8 text-center">
              {status === 'checking' && (
                <>
                  <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Loader2 className="w-10 h-10 text-gold animate-spin" />
                  </div>
                  <h1 className="font-display font-bold text-2xl text-deep-green mb-3">
                    Processing Your Payment
                  </h1>
                  <p className="text-muted-foreground mb-6">
                    Please wait while we confirm your payment...
                  </p>
                  <div className="flex items-center justify-center gap-1">
                    {[...Array(maxPolls)].map((_, i) => (
                      <div 
                        key={i} 
                        className={`w-2 h-2 rounded-full transition-colors ${
                          i < pollCount ? 'bg-gold' : 'bg-gray-200'
                        }`} 
                      />
                    ))}
                  </div>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h1 className="font-display font-bold text-2xl text-deep-green mb-3">
                    Welcome to the Alliance!
                  </h1>
                  <p className="text-muted-foreground mb-6">
                    Your membership has been activated successfully.
                  </p>
                  
                  {paymentData?.metadata && (
                    <div className="bg-bone p-4 rounded-sm mb-6">
                      <div className="flex items-center justify-center gap-3 mb-3">
                        {getTierIcon()}
                        <span className="font-display font-bold text-xl text-deep-green">
                          {paymentData.metadata.tier_name}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Amount paid: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                      </p>
                    </div>
                  )}

                  <div className="space-y-3">
                    <Link to="/dashboard">
                      <Button className="w-full btn-gold" data-testid="goto-dashboard-btn">
                        Go to Dashboard
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/member-card">
                      <Button variant="outline" className="w-full border-fairway text-fairway hover:bg-fairway hover:text-white">
                        View Member Card
                        <CreditCard className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </>
              )}

              {status === 'failed' && (
                <>
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <XCircle className="w-10 h-10 text-red-600" />
                  </div>
                  <h1 className="font-display font-bold text-2xl text-deep-green mb-3">
                    Payment Not Completed
                  </h1>
                  <p className="text-muted-foreground mb-6">
                    We couldn't confirm your payment. This may be temporary — please check your email for confirmation or try again.
                  </p>
                  <div className="space-y-3">
                    <Link to="/membership">
                      <Button className="w-full btn-gold">
                        Try Again
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline" className="w-full">
                        Return Home
                      </Button>
                    </Link>
                  </div>
                </>
              )}

              {status === 'expired' && (
                <>
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <XCircle className="w-10 h-10 text-amber-600" />
                  </div>
                  <h1 className="font-display font-bold text-2xl text-deep-green mb-3">
                    Session Expired
                  </h1>
                  <p className="text-muted-foreground mb-6">
                    Your payment session has expired. Please try again.
                  </p>
                  <Link to="/membership">
                    <Button className="w-full btn-gold">
                      Return to Membership
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MembershipSuccessPage;
